import React, {
  useState, useMemo, useRef, useEffect,
} from 'react';
import {useRouter} from 'next/router';
import {createPortal} from 'react-dom';
import qs from 'query-string';
import _ from 'lodash';
import moment from 'moment-timezone';

import {
  GuideCard, GuidesCtaCard, CompanyCarousel, CompanyCarousels, PlaceholderSection,
} from './CompanyCard';
import Button, {ButtonTypes} from '../../components/ButtonNew';
import DocumentHeader from '../../components/DocumentHeader';
import SignupModal from '../../components/SignupModal';
import {SearchIcon} from '../../components/svg';
import {MobileAwareFadeOut} from '../../components/FadeOut';
import {useAnalytics, useAppContext, useWindowSize} from '../../hooks';
import {roleToRoleCategoryOptions, expandRole, cx} from '../../util';

const featuredCompanies = ['anthropic', 'openai', 'google', 'meta-facebook', 'stripe', 'apple', 'amazon', 'netflix', 'microsoft'];


const SegmentedSearchBar = ({
  role, company, flatRoles, companyOptions, onRoleChange, onCompanyChange,
}) => {
  const [roleInput, setRoleInput] = useState(
    () => flatRoles.find((r) => r.value === role)?.label || '',
  );
  const [companyInput, setCompanyInput] = useState(
    () => companyOptions.find((c) => c.value === company)?.label || '',
  );
  const [activeSegment, setActiveSegment] = useState(null);
  const [mobileFilter, setMobileFilter] = useState('');
  const containerRef = useRef(null);
  const roleInputRef = useRef(null);
  const companyInputRef = useRef(null);
  const portalRef = useRef(null);
  const {width} = useWindowSize();
  const isMobile = width !== null && width < 768;
  const currentRef = useRef({
    role, company, flatRoles, companyOptions,
  });
  useEffect(() => {
    currentRef.current = {
      role, company, flatRoles, companyOptions,
    };
  });

  useEffect(() => {
    setRoleInput(flatRoles.find((r) => r.value === role)?.label || '');
  }, [role, flatRoles]);

  useEffect(() => {
    setCompanyInput(companyOptions.find((c) => c.value === company)?.label || '');
  }, [company, companyOptions]);

  useEffect(() => {
    const onMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        if (portalRef.current && portalRef.current.contains(e.target)) return;
        const {
          role: r, company: c, flatRoles: fr, companyOptions: co,
        } = currentRef.current;
        setActiveSegment(null);
        setRoleInput(fr.find((o) => o.value === r)?.label || '');
        setCompanyInput(co.find((o) => o.value === c)?.label || '');
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const filteredRoles = useMemo(() => {
    const selectedLabel = flatRoles.find((r) => r.value === role)?.label || '';
    if (!roleInput || roleInput === selectedLabel) return flatRoles;
    const q = roleInput.toLowerCase();
    return flatRoles.filter((r) => r.label.toLowerCase().includes(q));
  }, [flatRoles, roleInput, role]);

  const filteredCompanies = useMemo(() => {
    const selectedLabel = companyOptions.find((c) => c.value === company)?.label || '';
    if (!companyInput || companyInput === selectedLabel) return companyOptions;
    const q = companyInput.toLowerCase();
    return companyOptions.filter((c) => c.label.toLowerCase().includes(q));
  }, [companyOptions, companyInput, company]);

  const selectRole = (value, label) => {
    setRoleInput(label || '');
    onRoleChange(value);
    setActiveSegment(null);
  };

  const selectCompany = (value, label) => {
    setCompanyInput(label || '');
    onCompanyChange(value);
    setActiveSegment(null);
  };

  const closeMobileSheet = () => {
    const {
      role: r, company: c, flatRoles: fr, companyOptions: co,
    } = currentRef.current;
    setActiveSegment(null);
    setMobileFilter('');
    setRoleInput(fr.find((o) => o.value === r)?.label || '');
    setCompanyInput(co.find((o) => o.value === c)?.label || '');
  };

  const mobileItems = useMemo(() => {
    const base = activeSegment === 'role' ? filteredRoles : filteredCompanies;
    if (!mobileFilter) return base;
    const q = mobileFilter.toLowerCase();
    return base.filter(({label}) => label.toLowerCase().includes(q));
  }, [activeSegment, filteredRoles, filteredCompanies, mobileFilter]);
  const mobileSelected = activeSegment === 'role' ? role : company;
  const mobileSelect = activeSegment === 'role' ? selectRole : selectCompany;
  const mobileAnyLabel = activeSegment === 'role' ? 'Any role' : 'Any company';

  return (
    <div ref={containerRef} className="relative max-w-2xl mx-auto">
      {/* Bar */}
      <div className={cx(
        'flex items-center rounded-2xl border bg-white transition-all',
        activeSegment ? 'border-gray-300 shadow-xl' : 'border-gray-200 shadow-md',
      )}
      >
        {/* Role segment */}
        <div
          className={cx(
            'flex-1 rounded-l-2xl',
            activeSegment === 'role' ? 'bg-white' : 'hover:bg-gray-50',
          )}
        >
          <input
            ref={roleInputRef}
            className={cx('w-full px-6 py-3 text-sm placeholder-gray-400 bg-transparent focus:outline-none cursor-text', roleInput ? 'text-gray-900' : 'text-gray-500')}
            placeholder="Any role"
            value={roleInput}
            readOnly={isMobile}
            onChange={(e) => {
              setRoleInput(e.target.value);
              setActiveSegment('role');
              if (!e.target.value) onRoleChange(null);
            }}
            onFocus={() => setActiveSegment('role')}
            onClick={() => setActiveSegment('role')}
          />
        </div>

        <div className="w-px h-8 bg-gray-200 flex-shrink-0" />

        {/* Company segment */}
        <div
          className={cx(
            'relative flex-1',
            activeSegment === 'company' ? 'bg-white' : 'hover:bg-gray-50',
          )}
        >
          <input
            ref={companyInputRef}
            className={cx('w-full px-6 py-3 text-sm placeholder-gray-400 bg-transparent focus:outline-none cursor-text', companyInput ? 'text-gray-900' : 'text-gray-500')}
            placeholder="Any company"
            value={companyInput}
            readOnly={isMobile}
            onChange={(e) => {
              setCompanyInput(e.target.value);
              setActiveSegment('company');
              if (!e.target.value) onCompanyChange(null);
            }}
            onFocus={() => setActiveSegment('company')}
            onClick={() => setActiveSegment('company')}
          />

          {/* Company dropdown - desktop only */}
          {!isMobile && activeSegment === 'company' && (
            <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-50">
              <div className="py-2 max-h-72 overflow-y-auto">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectCompany(null, '')}
                  className={cx(
                    'w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-gray-50',
                    !company ? 'font-semibold text-gray-900' : 'text-gray-600',
                  )}
                >
                  Any company
                </button>
                {filteredCompanies.map(({value, label}) => (
                  <button
                    key={value}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectCompany(value, label)}
                    className={cx(
                      'w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-gray-50',
                      company === value ? 'font-semibold text-gray-900' : 'text-gray-600',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Search button */}
        <div className="pr-2 flex-shrink-0">
          <button
            type="button"
            className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
            onClick={() => setActiveSegment(null)}
          >
            <SearchIcon className="w-4 h-4" stroke="currentColor" />
          </button>
        </div>
      </div>

      {/* Role dropdown - desktop only */}
      {!isMobile && activeSegment === 'role' && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-50">
          <div className="py-2 max-h-72 overflow-y-auto">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectRole(null, '')}
              className={cx(
                'w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-gray-50',
                !role ? 'font-semibold text-gray-900' : 'text-gray-600',
              )}
            >
              Any role
            </button>
            {filteredRoles.map(({value, label}) => (
              <button
                key={value}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectRole(value, label)}
                className={cx(
                  'w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-gray-50',
                  role === value ? 'font-semibold text-gray-900' : 'text-gray-600',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Mobile bottom sheet */}
      {isMobile && activeSegment && createPortal(
        <div ref={portalRef}>
          <button
            type="button"
            aria-label="Close"
            className="fixed inset-0 bg-black/20 z-40"
            onClick={closeMobileSheet}
          />
          <div
            className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-50 max-h-[70vh] flex flex-col shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label={`Select ${mobileAnyLabel.toLowerCase().replace('any ', '')}`}
          >
            <div className="px-4 pt-4 pb-2 flex-shrink-0">
              <input
                type="text"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                placeholder={`Search ${mobileAnyLabel.toLowerCase().replace('any ', '')}...`}
                value={mobileFilter}
                onChange={(e) => setMobileFilter(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 bg-gray-50"
              />
            </div>
            <div className="overflow-y-auto flex-1 pb-2">
              <button
                type="button"
                onClick={() => { mobileSelect(null, ''); setMobileFilter(''); }}
                className={cx(
                  'w-full text-left px-4 py-3 text-sm transition-colors hover:bg-gray-50',
                  !mobileSelected ? 'font-semibold text-gray-900' : 'text-gray-600',
                )}
              >
                {mobileAnyLabel}
              </button>
              {mobileItems.map(({value, label}) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => { mobileSelect(value, label); setMobileFilter(''); }}
                  className={cx(
                    'w-full text-left px-4 py-3 text-sm transition-colors hover:bg-gray-50',
                    mobileSelected === value ? 'font-semibold text-gray-900' : 'text-gray-600',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body,
      )}

    </div>
  );
};

const GuidesPage = ({
  guides = [], roleOptions = [], roleCategories = [], companyOptions = [],
}) => {
  const router = useRouter();
  const {role, company} = router.query;
  const {tracker} = useAnalytics();
  const {currentUser, hasSubscription} = useAppContext();
  const [modalOpen, setModalOpen] = useState(false);
  const onCloseModal = () => setModalOpen(false);
  const showLocks = !!currentUser && !hasSubscription;

  useEffect(() => {
    if (!router.isReady) return;
    tracker.viewGuidesPage({role, company});
  }, [router.isReady]);

  const onClick = (e, guide) => {
    tracker.guidesClickGuide({guide: guide.name});
    if (!currentUser) {
      e.preventDefault();
      tracker.guidesViewSignupWall({guide: guide.name});
      setModalOpen(true);
    }
  };

  const allRoles = useMemo(
    () => roleToRoleCategoryOptions(roleOptions, roleCategories),
    [roleOptions, roleCategories],
  );

  const flatRoles = useMemo(
    () => _.flatMap(allRoles, (o) => o.options ?? [o]),
    [allRoles],
  );

  const roleLabel = useMemo(
    () => flatRoles.find((r) => r.value === role)?.label,
    [flatRoles, role],
  );
  const pageTitle = roleLabel ? `${roleLabel} Interview Guides` : 'Interview Guides';

  const companyLogoMap = useMemo(() => {
    const map = {};
    guides.forEach((g) => {
      if (g.companyTag && g.companyLogo) map[g.companyTag] = g.companyLogo;
    });
    return map;
  }, [guides]);

  // Filter guides
  const filteredGuides = useMemo(() => {
    const roleTags = expandRole(role);
    const filtered = guides.filter((guide) => {
      if (roleTags.length && !roleTags.includes(guide.roleTag)) return false;
      if (company && guide.companyTag !== company) return false;
      return true;
    });
    return _.sortBy(filtered, (g) => {
      const featuredIdx = featuredCompanies.indexOf(g.companyTag);
      return [showLocks && !g.free ? 1 : 0, featuredIdx >= 0 ? featuredIdx : 1000];
    });
  }, [guides, role, company, showLocks]);

  // Group by company: featured first, then by guide count desc, single-guide companies collapsed
  const guidesByCompany = useMemo(() => {
    const grouped = _.groupBy(filteredGuides, 'companyName');
    const withMeta = Object.keys(grouped).filter(Boolean).map((name) => ({
      name,
      guides: showLocks ? _.sortBy(grouped[name], (g) => (g.free ? 0 : 1)) : grouped[name],
      featuredIndex: featuredCompanies.indexOf(grouped[name][0]?.companyTag),
    }));

    const [featured, rest] = _.partition(withMeta, (c) => c.featuredIndex >= 0);
    const [multi, single] = _.partition(rest, (c) => c.guides.length > 1);

    const result = [
      ..._.sortBy(featured, 'featuredIndex'),
      ..._.orderBy(multi, (c) => c.guides.length, 'desc'),
    ].map(({name, guides: g}) => ({companyName: name, guides: g}));

    if (single.length > 0) {
      result.push({companyName: 'Other guides', guides: _.flatMap(single, 'guides')});
    }

    return result;
  }, [filteredGuides, showLocks]);

  // Role-only filtered guides — used for the pill so it reflects the h1 title
  const roleFilteredGuides = useMemo(
    () => guides.filter((g) => !role || g.roleTag === role),
    [guides, role],
  );

  const latestUpdate = useMemo(() => {
    const dates = roleFilteredGuides.map((g) => g.updatedAt).filter(Boolean);
    return dates.length > 0 ? moment(_.max(dates)).fromNow() : null;
  }, [roleFilteredGuides]);

  const handleRoleFilter = (tag) => {
    router.push({
      pathname: '/guides',
      search: qs.stringify({role: tag || undefined, company: company || undefined}),
    }, null, {shallow: true});
  };

  const handleCompanyFilter = (tag) => {
    router.push({
      pathname: '/guides',
      search: qs.stringify({role: role || undefined, company: tag || undefined}),
    }, null, {shallow: true});
  };

  const noResults = filteredGuides.length === 0;
  const isFiltered = !!(role || company);

  const sameRoleGuides = useMemo(() => {
    if (!noResults || !role) return [];
    const roleTags = expandRole(role);
    const filtered = guides.filter((g) => roleTags.includes(g.roleTag));
    return _.sortBy(filtered, (g) => {
      const idx = featuredCompanies.indexOf(g.companyTag);
      return idx >= 0 ? idx : 1000;
    });
  }, [noResults, role, guides]);

  const sameCompanyGuides = useMemo(() => {
    if (!noResults || !company) return [];
    const filtered = guides.filter((g) => g.companyTag === company);
    return _.sortBy(filtered, (g) => {
      const idx = featuredCompanies.indexOf(g.companyTag);
      return idx >= 0 ? idx : 1000;
    });
  }, [noResults, company, guides]);

  const otherRoleGuides = useMemo(() => {
    if (noResults || !role || !company) return [];
    const roleTags = expandRole(role);
    const filtered = guides.filter((g) => roleTags.includes(g.roleTag) && g.companyTag !== company);
    return _.sortBy(filtered, (g) => {
      const idx = featuredCompanies.indexOf(g.companyTag);
      return idx >= 0 ? idx : 1000;
    });
  }, [noResults, role, company, guides]);

  const suggestedCompanyName = companyOptions.find((c) => c.value === company)?.label;

  return (
    <div className="pt-10 pb-20 px-4 bg-white min-h-screen">
      <DocumentHeader title={pageTitle} />

      {/* Hero */}
      <div className="max-w-5xl mx-auto text-center mb-12">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
          {pageTitle}
        </h1>

        <p className="text-gray-500 text-base md:text-lg max-w-xl mx-auto mb-6">
          Learn how top companies run their interview loops with our
          comprehensive company guides.
        </p>

        {filteredGuides.length > 0 && (
          <div className="inline-flex items-center gap-1.5 text-sm text-gray-500 border border-gray-200 rounded-full px-3 py-1 mb-6">
            <span className="w-2 h-2 rounded-full flex-shrink-0 bg-green-500" />
            <span>
              {filteredGuides.length}
              {' '}
              {filteredGuides.length === 1 ? 'guide' : 'guides'}
              {latestUpdate && (
                <>
                  {' · Updated '}
                  {latestUpdate}
                </>
              )}
            </span>
          </div>
        )}

        <SegmentedSearchBar
          role={role}
          company={company}
          flatRoles={flatRoles}
          companyOptions={companyOptions}
          onRoleChange={handleRoleFilter}
          onCompanyChange={handleCompanyFilter}
        />

        {/* Company chiclets */}
        {companyOptions.length > 0 && (
          <div className="mt-3 max-w-2xl mx-auto">
            <MobileAwareFadeOut>
              {companyOptions.map(({label, value: companyValue}) => {
                const isActive = company === companyValue;
                const logo = companyLogoMap[companyValue];
                return (
                  <button
                    key={companyValue}
                    type="button"
                    onClick={() => handleCompanyFilter(isActive ? null : companyValue)}
                    className={cx(
                      'shrink-0 flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap border shadow-sm transition-colors',
                      isActive
                        ? 'border-indigo-500 text-indigo-700'
                        : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50',
                    )}
                  >
                    {logo && (
                      <img src={logo} alt={label} className="w-4 h-4 object-contain" />
                    )}
                    {label}
                  </button>
                );
              })}
            </MobileAwareFadeOut>
          </div>
        )}
      </div>

      {/* Guide carousels / grid */}
      <div className={cx('mx-auto', isFiltered ? 'max-w-6xl' : 'max-w-5xl')}>
        {!guides.length && (
          <>
            <PlaceholderSection />
            <PlaceholderSection />
          </>
        )}
        {guides.length > 0 && noResults && (
          <>
            <div className="border border-gray-200 rounded-2xl py-16 px-6 text-center mb-12">
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-gray-600">
                  <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M8 7h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">No guides</h3>
              <p className="text-sm text-gray-500 mb-5">
                We don&apos;t have any interview guides for this query yet
              </p>
              <Button
                href="https://airtable.com/appROifRg71Nsizjd/shrH6423pjEJEdqZg"
                buttonType={ButtonTypes.SKEUOMORPHIC}
              >
                Request guide
              </Button>
            </div>
            {(sameRoleGuides.length > 0 || sameCompanyGuides.length > 0) && (
              <>
                {sameRoleGuides.length > 0 && (
                  <CompanyCarousel
                    companyName={`${roleLabel} guides at other companies`}
                    guides={sameRoleGuides}
                    onClick={onClick}
                    showLocks={showLocks}
                  />
                )}
                {sameCompanyGuides.length > 0 && (
                  <CompanyCarousel
                    companyName={`More ${suggestedCompanyName} guides`}
                    guides={sameCompanyGuides}
                    onClick={onClick}
                    showLocks={showLocks}
                  />
                )}
              </>
            )}
          </>
        )}
        {guides.length > 0 && !noResults && (
          isFiltered ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredGuides.map((guide) => (
                  <GuideCard
                    key={guide.slug}
                    guide={guide}
                    onClick={onClick}
                    isLocked={showLocks && !guide.free}
                    className="w-full"
                  />
                ))}
                <GuidesCtaCard />
              </div>
              {otherRoleGuides.length > 0 && (
                <div className="mt-12">
                  <CompanyCarousel
                    companyName={`${roleLabel} guides at other companies`}
                    guides={otherRoleGuides}
                    onClick={onClick}
                    showLocks={showLocks}
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <CompanyCarousels
                guidesByCompany={guidesByCompany}
                onClick={onClick}
                showLocks={showLocks}
              />
              {otherRoleGuides.length > 0 && (
                <div className="mt-12">
                  <CompanyCarousel
                    companyName={`${roleLabel} guides at other companies`}
                    guides={otherRoleGuides}
                    onClick={onClick}
                    showLocks={showLocks}
                  />
                </div>
              )}
            </>
          )
        )}
      </div>

      {!currentUser && (
        <SignupModal
          source="guides"
          isOpen={modalOpen}
          dismissible
          onClose={onCloseModal}
        />
      )}
    </div>
  );
};

export default GuidesPage;
